import { Box, Stack, useMantineColorScheme } from "@mantine/core";

import type { FC } from "react";

export type PlaceholderLine = {
  /** Width of the line (px or CSS). Defaults to the parent `w` for the first line, "70%" for others. */
  w?: number | string;
  /** Height of the line (px). Defaults to 8 for the first line, 6 for others. */
  h?: number;
  /** Color variant. Defaults to alternating primary/secondary. */
  color?: "primary" | "secondary";
};

export type PlaceholderValueProps = {
  /** Default width for the first line (px or CSS). Defaults to 120. */
  w?: number | string;
  /** Number of lines (simple mode) or per-line configs (detailed mode). Defaults to 1. */
  lines?: number | PlaceholderLine[];
  /** Optional background color for every line. */
  color?: string;
  /** Optional background color for the secondary placeholder lines. */
  secondaryColor?: string;
  /** Optional gap between lines. Defaults to 4. */
  gap?: number | string;
};

/**
 * Renders skeleton-like placeholder bars to substitute missing values
 * inside LabelValue / DataGrid rows. Mirrors the visual style used in
 * DashboardWatchlistEmptyState.
 *
 * Usage:
 *   <PlaceholderValue lines={2} />                          // simple
 *   <PlaceholderValue lines={[{ w: 120 }, { w: 80, h: 4 }]} /> // custom
 */
export const PlaceholderValue: FC<PlaceholderValueProps> = ({
  w = 120,
  lines = 1,
  color,
  secondaryColor,
  gap = 4,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const bg = isDark ? "#333333" : "#EFEFEF";
  const bg2 = secondaryColor ?? (isDark ? "#3F3F3F" : "#D9D9D9");

  const configList: PlaceholderLine[] =
    typeof lines === "number" ? Array.from({ length: lines }).map(() => ({})) : lines;

  return (
    <Stack gap={gap} w="100%" maw={200}>
      {configList.map((line, index) => {
        const isFirst = index === 0;
        const colorKey = line.color ?? (index % 2 === 0 ? "primary" : "secondary");

        return (
          <Box
            key={index}
            h={line.h ?? (isFirst ? 10 : 6)}
            w={line.w ?? (isFirst ? w : "70%")}
            style={{
              borderRadius: 3,
              backgroundColor:
                color ??
                (colorKey === "primary"
                  ? bg
                  : line.color
                    ? line.color === "primary"
                      ? bg
                      : bg2
                    : bg2),
            }}
          />
        );
      })}
    </Stack>
  );
};
